import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import DownloadAppButton from "../../../elements/V2/Buttons/DownloadAppButton"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Typography from "../../../elements/V2/Typography"
import LayoutItem from "../../Layouts/LayoutItem"

import React from "react"

type Props = {
  textColor: Color
  titleText: string
  bodyText?: string
  imageUrl: string
  imageAlt: string
  mobileImageUrl?: string
  mobileImageAlt?: string
  buttonText: string
  buttonLink: string | YocoCom.YocoLink | undefined
  showDownloadAppButton: boolean
  buttonBackgroundColor: Color
  contentAlignment: "left" | "center"
  mobileGridOrder?: number
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
}

const RichMedia = ({
  textColor,
  titleText,
  bodyText,
  imageUrl,
  imageAlt,
  mobileImageUrl,
  mobileImageAlt,
  buttonText,
  buttonLink,
  showDownloadAppButton = false,
  buttonBackgroundColor,
  contentAlignment,
  mobileGridOrder,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  ...props
}: Props) => {
  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      className={classNames(
        "relative col-span-full inline-block h-full w-full"
      )}
      {...props}
    >
      <div className="absolute left-0 top-0 h-full w-full">
        <GatsbyStoryblokImage
          image={imageUrl}
          alt={imageAlt || "Rich media background image"}
          loadType="eager"
          quality={80}
          aspectRatio="fullWidth"
          rounded
          className="hidden h-full w-auto sm:block"
        />

        <div className="h-full sm:hidden">
          <GatsbyStoryblokImage
            rounded
            image={mobileImageUrl ?? imageUrl}
            alt={mobileImageAlt ?? imageAlt}
            loadType="eager"
            quality={80}
            aspectRatio="1:1"
            className="h-full w-auto"
          />
        </div>
      </div>

      <div
        className={classNames(
          "relative flex h-full w-full flex-col",
          {
            "items-center justify-center px-24 py-80 md:py-164 lg:py-[196px]":
              contentAlignment === "center",
          },
          {
            "md:p32p items-start justify-end p-24 pt-[138px] md:pt-[260px] lg:p-48 lg:pt-[340px]":
              contentAlignment === "left",
          }
        )}
      >
        <Typography
          text={titleText}
          font="grotesk"
          weight="medium"
          size="h2"
          color={textColor}
          className={classNames("mb-8", {
            "text-center": contentAlignment === "center",
          })}
          element="h2"
        />
        {bodyText && (
          <Typography
            text={bodyText}
            font="grotesk"
            weight="book"
            size="lead-md"
            color={textColor}
            className={classNames("mb-24 lg:mb-32", {
              "text-center": contentAlignment === "center",
            })}
          />
        )}

        {showDownloadAppButton ? (
          <DownloadAppButton text={buttonText} color={buttonBackgroundColor} />
        ) : (
          <PillButton
            size="large"
            style="solid"
            text={buttonText}
            color={buttonBackgroundColor}
            linkUrl={buttonLink}
            trackingEvent={trackingEvent}
            trackingEventKey={trackingEventKey}
            trackingEventValue={trackingEventValue}
          />
        )}
      </div>
    </LayoutItem>
  )
}

export default RichMedia
